import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

type ClerkTokenGetter = (opts?: { forceRefresh?: boolean }) => Promise<string | null>;

// Module-level token getter set by ClerkTokenBridge component after ClerkProvider mounts
let _getToken: ClerkTokenGetter | null = null;

// Token cache — validity is driven by the JWT's own `exp` claim, never by how long
// ago we fetched it. Clerk's getToken() serves from Clerk's internal cache and only
// mints a new token when the current one is nearly dead, so a token handed to us can
// already be ~50s old; anchoring a TTL to our fetch time made us serve expired JWTs.
const REFRESH_MARGIN_MS = 30_000;
const TOKEN_WAIT_ATTEMPTS = 8;
const TOKEN_WAIT_DELAY_MS = 250;
let _cachedToken: string | null = null;
let _cachedExpiresAt = 0;
let _inflightToken: Promise<string | null> | null = null;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Returns the token's expiry in epoch ms, or 0 if it can't be read — an unreadable
// token counts as expired so we always refetch rather than serve something unusable.
function tokenExpiryMs(token: string): number {
  try {
    const payload = token.split(".")[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : 0;
  } catch (_) {
    return 0;
  }
}

function setAuthorizationHeader(config: { headers?: any }, token: string) {
  const headers = config.headers ?? {};
  const value = `Bearer ${token}`;

  if (typeof headers.set === "function") {
    headers.set("Authorization", value);
  } else {
    headers.Authorization = value;
  }

  config.headers = headers;
}

async function getCachedToken(forceRefresh = false): Promise<string | null> {
  if (!_getToken) return null;
  if (!forceRefresh && _cachedToken && _cachedExpiresAt - Date.now() > REFRESH_MARGIN_MS) {
    return _cachedToken;
  }
  // Dedupe concurrent refreshes, forced ones included, so a burst of 401 retries
  // coalesces into a single mint instead of one per in-flight request.
  if (_inflightToken) {
    return _inflightToken;
  }

  // Inside the margin we always skip Clerk's cache: asking without skipCache would
  // hand back the same near-expired token and leave us in the same window.
  const request = _getToken({ forceRefresh: true })
    .then((token) => {
      if (token) {
        _cachedToken = token;
        _cachedExpiresAt = tokenExpiryMs(token);
      }
      return token;
    })
    .finally(() => {
      if (_inflightToken === request) {
        _inflightToken = null;
      }
    });

  _inflightToken = request;
  return request;
}

async function getCachedTokenWithWait(forceRefresh = false): Promise<string | null> {
  for (let attempt = 0; attempt < TOKEN_WAIT_ATTEMPTS; attempt += 1) {
    const token = await getCachedToken(forceRefresh || attempt > 0);
    if (token) return token;
    await delay(TOKEN_WAIT_DELAY_MS);
  }

  return null;
}

export const setClerkTokenGetter = (fn: ClerkTokenGetter) => {
  if (_getToken === fn) return;
  _getToken = fn;
  _cachedToken = null;
  _cachedExpiresAt = 0;
  _inflightToken = null;
};

export const getAuthedToken = (
  forceRefresh = false,
  waitForToken = false
): Promise<string | null> =>
  waitForToken ? getCachedTokenWithWait(forceRefresh) : getCachedToken(forceRefresh);

function createAuthedAxios(opts?: { baseURL?: string }): AxiosInstance {
  const api = axios.create({ baseURL: opts?.baseURL ?? "/api" });

  api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await getCachedToken();
    if (token) {
      setAuthorizationHeader(config, token);
    }
    return config;
  });

  // On 401, retry once with a force-refreshed token to handle the Clerk
  // session-load window and the 60-second JWT rotation gap.
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalConfig = error.config;
      if (error.response?.status === 401 && originalConfig && !originalConfig._retry && _getToken) {
        originalConfig._retry = true;
        const token = await getCachedTokenWithWait(true);
        if (token) {
          setAuthorizationHeader(originalConfig, token);
          return api(originalConfig);
        }
      }
      return Promise.reject(error);
    }
  );

  return api;
}

// REACT_APP_HOST_IP is "http://localhost:8001/api/" — use it as the base so all
// requests go to the backend, not the frontend dev server.
export const axiosAuthed = createAuthedAxios({
  baseURL: process.env.REACT_APP_HOST_IP ?? "/api",
});
